import React, { useState } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";

const inputCls = "bg-charcoal-800 border-[#E2D4C1] rounded-none focus-visible:ring-gold";
const btnCls = "w-full bg-gold hover:bg-gold-hover text-white rounded-none uppercase tracking-[0.2em] text-xs font-semibold py-5";

// Steps: "request" (enter email) → "sent" (check inbox) ; emailed link /reset?token=… → "confirm" → "done"
export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [step, setStep] = useState(token ? "confirm" : "request");
    const [email, setEmail] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const requestReset = async (e) => {
        e.preventDefault(); setLoading(true);
        try {
            await api.post("/auth/reset/request", { email });
            setStep("sent");
        } catch { toast.error("Could not request a reset. Please try again."); }
        setLoading(false);
    };

    const confirmReset = async (e) => {
        e.preventDefault(); setLoading(true);
        try {
            await api.post("/auth/reset/confirm", { token, new_password: newPassword });
            toast.success("Password reset. You can sign in now.");
            setStep("done");
        } catch (err) {
            const detail = err.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Reset failed. Use at least 8 characters, or request a new link.");
        }
        setLoading(false);
    };

    return (
        <div data-testid="reset-page" className="max-w-md mx-auto px-6 py-24">
            <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-3">Account Recovery</div>
            <h1 className="font-display text-4xl mb-10">Reset Password</h1>

            {step === "request" && (
                <form onSubmit={requestReset} className="space-y-4">
                    <label htmlFor="reset-email" className="block text-xs text-[#6B645C]">Account email</label>
                    <Input id="reset-email" required type="email" data-testid="reset-email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className={inputCls} />
                    <Button type="submit" disabled={loading} data-testid="reset-request" className={btnCls}>{loading ? "Sending…" : "Email Me a Reset Link"}</Button>
                </form>
            )}
            {step === "sent" && (
                <div data-testid="reset-sent" className="space-y-3 text-[#33302C]">
                    <p>If an account exists for <strong>{email}</strong>, we've emailed a link to reset your password.</p>
                    <p className="text-xs text-[#6B645C]">The link is valid for 2 hours and can be used once. Check your spam folder if it doesn't arrive.</p>
                </div>
            )}
            {step === "confirm" && (
                <form onSubmit={confirmReset} className="space-y-4">
                    <label htmlFor="reset-new-password" className="block text-xs text-[#6B645C]">New password (at least 8 characters)</label>
                    <Input id="reset-new-password" required minLength={8} type="password" autoComplete="new-password" data-testid="reset-new-password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className={inputCls} />
                    <Button type="submit" disabled={loading} data-testid="reset-confirm" className={btnCls}>{loading ? "Resetting…" : "Set New Password"}</Button>
                </form>
            )}
            {step === "done" && (
                <div data-testid="reset-success" className="text-center">
                    <div className="text-gold font-display text-2xl mb-3">Password updated.</div>
                    <Link to="/login" className="text-sm uppercase tracking-widest text-gold hover:underline">Sign In</Link>
                </div>
            )}
        </div>
    );
}
